import { useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Image } from '@/shared/ui/image/Image';
import { Button } from '@/shared/ui/form/button/button';
import {
  Content,
  Root,
  ContentHeader,
  Item,
  Status,
  List,
  UnreadDot,
  BoxTime,
  StatusIcon,
  BellButton,
  NotificationImage,
} from './styles';
import {
  BodyTextMediumB,
  BodyTextRegularB,
  BodyTextMediumA,
  BodyTextRegularC,
} from '@/shared/ui/typography2';
import { Box } from '@/shared/ui/box/Box';
import { Badge } from '@/shared/ui/badge';
import {
  useNotifyList,
  useNotificationDate,
} from '@/features/notify-management/model/hooks';
import { useI18n } from '@locales/client';
import { Condition } from '@/shared/ui/condition/condition';
import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';
import { timestampToDate } from '@/shared/libs/timestamp-to-date';
import { getMediaQueryStore } from '@/shared/libs/get-media-query-store';
import { MOBILE_BREAKPOINT } from '@/shared/hooks/useBreakpoints';

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;

const NotifyManagement = () => {
  const {
    notifyList,
    isNotifyListLoading,
    markReadAllTrigger,
    notifyListMutate,
  } = useNotifyList();

  const t = useI18n();
  const formatUrl = useLocaleFormat();

  const { groupedNotifications, formatGroupHeader, formatNotificationTime } =
    useNotificationDate(notifyList);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (isNotifyListLoading) {
    return null;
  }

  const newNotifyList = Array.isArray(notifyList)
    ? notifyList.filter((item) => !item?.read)
    : [];

  const markReadAll = async (isOpened) => {
    if (getMediaQueryStore(MOBILE_QUERY).getSnapshot()) {
      document.body.style.overflow = isOpened ? 'hidden' : 'auto';
    }

    if (isOpened && newNotifyList?.length) {
      await markReadAllTrigger();
    }
    if (!isOpened && newNotifyList?.length) {
      await notifyListMutate();
    }
  };

  const popoverCloseStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  return (
    <Root onOpenChange={(bool) => markReadAll(bool)}>
      <Popover.Trigger asChild>
        <Box $display="flex" $alignItems="center">
          <Box $position="relative" $flex="auto">
            <BellButton
              type="button"
              variant="gray"
              icon={
                <Image
                  style={{ display: 'flex' }}
                  width={20}
                  height={20}
                  src="/images/icons/bell-icon.svg"
                  alt="bell-icon"
                />
              }
            />
            <Condition match={newNotifyList?.length > 0}>
              <Badge color="#dd3636" text={newNotifyList?.length} />
            </Condition>
          </Box>
        </Box>
      </Popover.Trigger>

      <Content align="end" sideOffset={8}>
        <ContentHeader>
          <Box $display="flex" $gap={10}>
            <BodyTextMediumA>{t('notifications.title')}</BodyTextMediumA>
          </Box>
          <Popover.Close style={popoverCloseStyle}>
            <Image
              src="/images/icons/close-dialog-secondary.svg"
              width={20}
              height={20}
              alt="close-dialog-secondary"
            />
          </Popover.Close>
        </ContentHeader>
        <Condition match={groupedNotifications?.length > 0}>
          <List>
            {groupedNotifications?.map((group) => (
              <Box key={group?.date?.toISOString()}>
                <Box $textAlign="center" $marginBottom="8px">
                  <BodyTextRegularB color="#AFAFAF">
                    {formatGroupHeader(group?.date)}
                  </BodyTextRegularB>
                </Box>

                <Condition match={group?.items?.length > 0}>
                  <Box $display="flex" $flexDirection="column" $gap={8}>
                    {group?.items?.map((item) => {
                      const shouldDisplayMedia =
                        Boolean(item?.image) || Boolean(item?.action);

                      return (
                        <Item
                          key={item?.ts}
                          $status={item?.status}
                          href={item?.link ? formatUrl(item?.link) : '#'}
                        >
                          <Box
                            $display="flex"
                            $flexDirection="column"
                            $gap={12}
                            $width="100%"
                          >
                            <Box $display="flex" $gap={8}>
                              <Status $status={item?.status}>
                                <Condition match={!item?.read}>
                                  <UnreadDot />
                                </Condition>
                                <StatusIcon
                                  $status={item?.status}
                                  src={`/images/icons/toast/${item?.type}.svg`}
                                  width={24}
                                  height={24}
                                />
                              </Status>
                              <Box
                                $display="flex"
                                $flexDirection="column"
                                $gap={4}
                                $maxWidth="calc(100% - 85px)"
                                $overflow="hidden"
                              >
                                <BodyTextMediumA>
                                  {item?.title}
                                </BodyTextMediumA>
                                <BodyTextRegularB>
                                  {item?.text}
                                </BodyTextRegularB>
                              </Box>
                            </Box>

                            <Box
                              $display={shouldDisplayMedia ? 'flex' : 'none'}
                              $flexDirection="column"
                              $gap={12}
                            >
                              <Condition match={Boolean(item?.image)}>
                                <NotificationImage
                                  src={item?.image}
                                  width="100%"
                                  alt={item?.image}
                                />
                              </Condition>

                              <Condition match={Boolean(item?.action)}>
                                <Box
                                  $display="flex"
                                  $alignItems="center"
                                  $justifyContent="flex-start"
                                  $width="max-content"
                                >
                                  <Button variant="orangeGradient" type="button">
                                    <BodyTextMediumB>
                                      {item?.action}
                                    </BodyTextMediumB>
                                  </Button>
                                </Box>
                              </Condition>
                            </Box>
                          </Box>

                          <BoxTime>
                            <BodyTextRegularC color="#AFAFAF">
                              {formatNotificationTime(
                                timestampToDate(item?.ts)
                              )}
                            </BodyTextRegularC>
                          </BoxTime>
                        </Item>
                      );
                    })}
                  </Box>
                </Condition>
              </Box>
            ))}
          </List>
        </Condition>
      </Content>
    </Root>
  );
};

export { NotifyManagement };

// import dynamic from 'next/dynamic';
//
// const DynamicNotifyManagement = dynamic(() => import('./index').then((m) => m.NotifyManagement), {
//   loading: () => null,
//   ssr: false,
// });
//
// export default DynamicNotifyManagement;

